import React from "react";
import Menu from "@ui/Menu";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { AedLine } from "@ui/IconV2/react";
import OptionList from "@ui/OptionList";
import type { Project } from "../../../api/useProjects";
import { FragmentSlot, OptionV2 } from "@ui/Option/v2";
import { getEventStatusText } from "../../Project/Events/utils/faultEventStatus";
import formatDate from "@ui/i18n/formatDate";
import { css } from "@linaria/core";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import t from "../../../i18n";
import tui from "@ui/i18n";
import type { Event } from "../../../api/useEvents";
import { Tag } from "@ui/Tag";

interface Props {
  project: Project;
  events: Event[];
  selectedEventId: string | null;
  onSelect: (eventId: string | null) => void;
}

const EventMenu = ({ project, events, selectedEventId, onSelect }: Props) => {
  const [filterValue, setFilterValue] = React.useState<string>("");

  const options = React.useMemo(
    () =>
      events
        .filter(
          event =>
            filterValue.length === 0 ||
            event.name.toLowerCase().includes(filterValue.toLowerCase()),
        )
        .map(event => {
          const id = event.id;
          const description = t("comments.fault_event.menu.description", {
            startsAt: formatDate(new Date(event.startsAt), "slash", project.timezone),
            closedAt:
              "closedAt" in event && event.closedAt
                ? `${formatDate(new Date(event.closedAt), "slash", project.timezone)}`
                : null,
            status: getEventStatusText(event.metadata.status),
          });
          return {
            label: event.name,
            value: id,
            description,
            startsAt: event.startsAt,
            type: event.type,
          };
        })
        .sort((a, b) => {
          if (a.label !== b.label) return a.label.localeCompare(b.label);
          return new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime();
        }),
    [events, project.timezone, filterValue],
  );

  const selected = React.useCallback(
    (option: { label: string; value: string; description: string }) =>
      option.value === selectedEventId,
    [selectedEventId],
  );

  const handleOptionClick = React.useCallback(
    (option: { label: string; value: string; description: string }) => {
      if (option.value === selectedEventId) onSelect(null);
      else onSelect(option.value);
    },
    [onSelect, selectedEventId],
  );

  return (
    <Menu
      className={faultEventMenu}
      filtered
      onFilterChange={v => setFilterValue(v)}
      filterValue={filterValue}
      placement="bottom"
      overlay={
        <OptionList onRequestClose={() => {}}>
          {options.length === 0 ? (
            <div className={blankState}>{tui("filter.none")}</div>
          ) : (
            options.map(option => (
              <OptionV2
                key={option.value}
                className={labelOption}
                selected={selected(option)}
                onClick={() => handleOptionClick(option)}
                closeOnClick
              >
                <FragmentSlot slot="text">{option.label}</FragmentSlot>
                {option.description.length > 0 && (
                  <FragmentSlot slot="description">{option.description}</FragmentSlot>
                )}
                {option.type === "MANUAL" ? (
                  <FragmentSlot slot="extra">
                    <Tag size="s">{t("project.events.tag.manual")}</Tag>
                  </FragmentSlot>
                ) : null}
              </OptionV2>
            ))
          )}
        </OptionList>
      }
    >
      <div>
        <TooltipV2
          placement="bottom"
          overlay={<OverlayWrapper text={t("comments.tooltip.associate_event")} />}
        >
          <ButtonIconV2 title="Event" data-cy="edit-event" checked={selectedEventId !== null}>
            <AedLine size="s" />
          </ButtonIconV2>
        </TooltipV2>
      </div>
    </Menu>
  );
};

export default EventMenu;

// #region CSS
const faultEventMenu = css`
  width: 380px;
  max-height: 250px;
  height: 364px;
`;
const labelOption = css`
  & > * {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;

    @supports (-webkit-line-clamp: 2) {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: initial;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }
  }
`;
const blankState = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 10px;
  flex: 1;
  font-weight: 600;
`;
// #endregion CSS
